import { useEffect, useState } from "react";
import { Flex, Select, SelectProps, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";

import api, { ApprovalListProps } from "@/apis/order";
import { debounce } from "lodash";
import { SaleItemState } from "@/types/sales";
import { CellSelectWrap } from "@/pages/Sales/style";
import { PAGE_SIZE } from "@/utils/const";
import { CommonStateEnum } from "@/types/common";

interface SelectSaleItemOptionProps extends SelectProps {
  onChangeSaleItem?: (info: SaleItemState) => void;
  defaultList?: SaleItemState[];
}

const SelectSaleItemOption = ({
  onChangeSaleItem,
  onChange,
  defaultList = [],
  ...props
}: SelectSaleItemOptionProps) => {
  const { t } = useTranslation();

  const [list, setList] = useState<Array<SaleItemState>>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    fetch("", 1);
  }, []);

  useEffect(() => {
    if (defaultList.length > 0) setList(last => [...last, ...defaultList])
  }, [defaultList])

  const fetch = (keyword: string, current: number) => {
    setListLoading(true);
    const params: ApprovalListProps = {
      params: {
        body: {
          customerId: "",
          keyword: keyword || ""
        },
        pagination: { offset: (current - 1) * PAGE_SIZE, take: PAGE_SIZE }
      },
      orgId: "",
      state: CommonStateEnum.ACTIVE
    };
    api.approvalList(params)
      .then((res) => {
        if (current === 1) {
          setList(res?.body || []);
        } else if (res?.body) {
          setList([...list, ...res.body]);
        }
        setTotal(res.pagination?.size ?? 0);
      })
      .finally(() => {
        setListLoading(false);
      });
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      currentPage * PAGE_SIZE < total &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight
    ) {
      let page = currentPage + 1;
      fetch(keyword, page);
      setCurrentPage(page);
    }
  };

  const onSearch = debounce((val: string) => {
    setKeyword(val.trim());
    setCurrentPage(1);
    fetch(val, 1);
  }, 500);

  return (
    <CellSelectWrap style={{ flex: 1 }}>
      <Select
        filterOption={false}
        onSearch={onSearch}
        popupMatchSelectWidth={false}
        onDropdownVisibleChange={(open) => {
          if (open) {
            setKeyword("");
            setCurrentPage(1);
            fetch("", 1);
          }
        }}
        onChange={(val, option) => {
          onChange && onChange(val, option);
          onChangeSaleItem && onChangeSaleItem((option as any).data as SaleItemState)
        }}
        style={{ width: "100%" }}
        showSearch
        placeholder={t("sale.plSaleItem")}
        value={props.value}
        options={list.map(item => ({
          value: item.id,
          label: item.order?.number || '',
          data: item
        }))}
        optionRender={({ data }) => {
          const item = data.data as SaleItemState;
          const orderNumber = item.order?.number || '-';
          const internalNumber = item.order?.innerNumber || '';
          return (
            <Flex style={{ width: '100%' }} align='center' justify='space-between' gap={8}>
              <span>{orderNumber}</span>
              {internalNumber && <Tag color='blue'>{internalNumber}</Tag>}
            </Flex>
          );
        }}
        onPopupScroll={handleScroll}
        dropdownRender={(ele) => (
          <>
            {ele}
            {listLoading && (
              <Flex
                style={{ width: "100%", padding: 10 }}
                justify="center"
                align="center"
              >
                <Spin />
              </Flex>
            )}
          </>
        )}
        {...props}
      />
    </CellSelectWrap>
  );
};

export default SelectSaleItemOption;
